import $ from 'jquery';
import { Fancybox } from "@fancyapps/ui/dist/fancybox/";
import { Carousel } from "@fancyapps/ui/dist/carousel/";
import { Arrows } from "@fancyapps/ui/dist/carousel/carousel.arrows.js";
import { Dots } from "@fancyapps/ui/dist/carousel/carousel.dots.js";
import "@fancyapps/ui/dist/carousel/carousel.dots.css";
import "@fancyapps/ui/dist/carousel/carousel.css";
import "@fancyapps/ui/dist/fancybox/fancybox.css";
import "@fancyapps/ui/dist/carousel/carousel.arrows.css";
import Handlebars from 'handlebars';
import * as Sentry from "@sentry/browser";
/**
 * Tracks events in Umami.
 *
 * @param eventName Name of the event (e.g. "View Item").
 * @param eventData Optional key-value dictionary of properties (e.g. {variant: 'foo'}).
 */
export function umamiTrack(eventName, eventData) {
    if (!window.umami) {
        const error = new Error('Umami not initialized.');
        if (Sentry) {
            Sentry.captureException(error);
        }
        else {
            console.error(error);
        }
        return;
    }
    window.umami.track(eventName, eventData);
}
/**
 * Tracks events in Gtag and Umami.
 *
 * @param eventName Name of the event (e.g. "View Item").
 * @param eventData Optional key-value dictionary of properties (e.g. {variant: 'foo'}).
 */
export function track(eventName, eventData) {
    umamiTrack(eventName, eventData);
    gtag('event', eventName, eventData);
}
function requiredQuerySelector(selector) {
    const elm = document.querySelector(selector);
    if (!elm) {
        throw new Error(`Failed to locate required element: "${selector}".`);
    }
    return elm;
}
function convertProductToGA4Item(product) {
    return {
        item_id: product.id,
        item_name: product.name,
        item_category: product.category,
        price: product.price,
        quantity: product.quantity ?? 1,
    };
}
function convertProductsToGA4Items(products) {
    let items = [];
    for (const product of products) {
        items.push(convertProductToGA4Item(product));
    }
    return items;
}
function getProductsTotal(products) {
    let total = 0;
    for (const product of products) {
        total += product.price * (product.quantity ?? 1);
    }
    return total;
}
/**
 * Canonical payment-method label for analytics. `add_payment_info` reads the
 * `payment_method` form value (`lyra` / `paypal` / `bank_transfer`) while the
 * `purchase` event fires from a different page after the processor returns;
 * mapping both through this keeps GA4's close-rate-by-method a one-step query.
 */
const PAYMENT_TYPE_LABELS = {
    lyra: 'Card',
    paypal: 'PayPal',
    bank_transfer: 'Bank transfer',
};
function paymentTypeLabel(rawPaymentMethod) {
    return PAYMENT_TYPE_LABELS[rawPaymentMethod] ?? rawPaymentMethod;
}
/**
 * base.html
 * Initializes Sentry error tracking.
 *
 * Should be one of the first script to run.
 *
 * @param dsn Sentry DSN URL.
 * @param environment Deployement environement (e.g. "production").
 * @param release Release version (e.g. "1.0.0").
 * @param debug Whether this is the debug version or the production version.
 */
export function init(dsn, environment, release, debug) {
    try {
        // https://docs.sentry.io/platforms/javascript/
        // https://github.com/getsentry/sentry-javascript/tree/master/packages/browser
        Sentry.init({
            dsn, environment, release, debug,
            // Since v11 Sentry collects these by default (formerly opt-in via sendDefaultPii);
            // keep session cookies, headers and user IP out of events.
            dataCollection: {
                userInfo: false,
                cookies: false,
                httpHeaders: false,
            },
            integrations: [
                // Avoids splitting /js/bundle.js?v=123 by version.
                Sentry.rewriteFramesIntegration({
                    // This function runs on every stack frame before sending the error
                    iteratee: (frame) => {
                        if (frame.filename) {
                            // Split on '?' (query) or '#' (hash) and take the first part only
                            // Input:  "https://example.com/js/bundle.js?v=123"
                            // Output: "https://example.com/js/bundle.js"
                            frame.filename = frame.filename.split(/[?#]/)[0];
                        }
                        return frame;
                    },
                }),
                // Captures failed HTTP requests (fetch/XHR) as errors.
                Sentry.httpClientIntegration({
                    // Define which status codes should mark the transaction span as "failed".
                    // By default, Sentry might only consider 5xx failures.
                    failedRequestStatusCodes: [[400, 599]],
                    // Only instrument requests matching these regex patterns.
                    failedRequestTargets: [
                        /^https:\/\/[^\/]*libertys\.[a-z]+\//,
                        /^https:\/\/[^\/]*alc\.net\//,
                    ],
                }),
            ],
        });
    }
    catch (error) {
        // We can't do much but let's continue the script.
        console.error(error);
    }
    if (window.umami) {
        const params = new URLSearchParams(window.location.search);
        let referrer = document.referrer;
        const utmSource = params.get('utm_source');
        if (params.has('srsltid')) {
            referrer = 'https://Merchants.Google.com';
        }
        else if (utmSource) {
            switch (utmSource) {
                case 'chatgpt':
                case 'chatgpt.com':
                    referrer = 'https://ChatGPT.com';
                    break;
                case 'facebook':
                case 'facebook.com':
                    referrer = 'https://Facebook.com';
                    break;
                case 'instagram':
                case 'instagram.com':
                    referrer = 'https://Instagram.com';
                    break;
                case 'twitter':
                case 'twitter.com':
                case 't.co':
                    referrer = 'https://Twitter.com';
                    break;
                // default to using the utm_source if it's not in our list
                default:
                    referrer = utmSource;
                    break;
            }
        }
        window.umami.track(props => ({
            ...props,
            referrer: referrer
        }));
    }
    // https://fancyapps.com/fancybox/get-started/usage/
    Fancybox.bind('[data-fancybox]', {
        on: {
            'Carousel.change': (fancyboxRef, carouselRef) => {
                track('Zoom', {
                    carousel: fancyboxRef.getSlide()?.index,
                });
            },
        },
    });
    $('#langs li a, .locale-picker li a').on('click', () => {
        track('Change Language', {});
    });
    // base.html loads Umami data-auto-track="false" (so the pageview can be
    // sent once by hand with a rewritten referrer, above). That also stops
    // Umami installing its own [data-umami-event] click listener, so wire a
    // minimal equivalent: fire umamiTrack() for any clicked element carrying
    // data-umami-event, with its data-umami-event-* attributes as event data.
    document.addEventListener('click', (event) => {
        const target = event.target?.closest('[data-umami-event]');
        if (!target) {
            return;
        }
        const data = {};
        for (const name of target.getAttributeNames()) {
            const match = name.match(/^data-umami-event-(.+)$/);
            if (match) {
                data[match[1]] = target.getAttribute(name) ?? '';
            }
        }
        umamiTrack(target.dataset.umamiEvent, Object.keys(data).length ? data : undefined);
    });
}
/**
 * Measuring Product Impressions - Enhanced Ecommerce.
 * See https://support.google.com/analytics/answer/12200568?hl=en#zippy=%2Cgoogle-tag-manager-websites
 *
 * Only measure an impression when the element is actually shown for a short time.
 */
function logProductImpressions(elements, itemListName, currencyCode, category) {
    // https://developers.google.com/analytics/devguides/collection/ga4/reference/events?client_type=gtag#view_item_list
    let items = [];
    elements.forEach(element => {
        const ref = element.dataset.ref;
        const name = element.title;
        const price = element.dataset.price ? parseFloat(element.dataset.price) : undefined;
        const position = element.dataset.position ? parseInt(element.dataset.position, 10) : undefined;
        items.push({
            item_id: ref,
            item_name: name,
            price: price,
            index: position,
        });
    });
    gtag('event', 'view_item_list', {
        item_list_name: itemListName,
        items,
    });
}
/**
 * search_results.html
 * @param currencyCode Currency code.
 */
export function runSearchResults(currencyCode) {
    const hits = document.querySelectorAll('a.item').length;
    const query = $('form.search input').val();
    track(`Search`, {
        items_count: hits,
        // Logs also the result count because we cannot filter in Umami yet.
        query: `${hits}: ${query}`,
    });
    setupItemListingLogging('Search Results', currencyCode);
}
/**
 * category.html
 * @param categoryName Current category name.
 * @param currencyCode Currency code.
 */
export function runCategory(categoryName, currencyCode) {
    umamiTrack('View Category', {
        category: categoryName,
    });
    setupItemListingLogging('Category', currencyCode, categoryName);
}
/**
 * item.html
 * @param product Product displayed.
 * @param currencyCode Currency code.
 * @param itemInfoUrl URL to refresh the info.
 */
export function runItem(product, currencyCode, itemInfoUrl, detectCountry, isOutOfStock) {
    const ebayRatingShown = !!document.querySelector('.user-feedbacks');
    umamiTrack(isOutOfStock ? 'View Soldout Item' : 'View Item', {
        currency: currencyCode,
        id: product.id,
        category: product.category,
    });
    // Measuring Views of Product Details - Enhanced Ecommerce
    // See https://developers.google.com/analytics/devguides/collection/ga4/ecommerce?client_type=gtag#view_item_details
    // https://developers.google.com/analytics/devguides/collection/ga4/reference/events#view_item
    gtag('event', 'view_item', {
        currency: currencyCode,
        value: product.price,
        items: [convertProductToGA4Item(product)],
        item_list_name: 'Item details',
        out_of_stock: isOutOfStock,
    });
    const items = [convertProductToGA4Item(product)];
    // Add to cart event.
    $('a.additem').on('click', (evt) => {
        umamiTrack('Add to Cart', {
            currency: currencyCode,
            ...product,
            exp_ebay_rating_shown: ebayRatingShown,
        });
        // https://developers.google.com/analytics/devguides/collection/ga4/reference/events?client_type=gtag#add_to_cart
        gtag('event', 'add_to_cart', {
            currency: currencyCode,
            value: product.price,
            items: items,
        });
    });
    // Measuring Checkout.
    $('a.buynow').on('click', (evt) => {
        umamiTrack('Checkout Intent', {
            variant: 'buy_now',
            currency: currencyCode,
            cart_total: product.price,
            cart_items_count: 1,
            exp_ebay_rating_shown: ebayRatingShown,
        });
        // https://developers.google.com/analytics/devguides/collection/ga4/reference/events?client_type=gtag#add_to_cart
        gtag('event', 'add_to_cart', {
            currency: currencyCode,
            value: product.price,
            items: items,
        });
        // https://developers.google.com/analytics/devguides/collection/ga4/reference/events?client_type=gtag#begin_checkout
        gtag('event', 'begin_checkout', {
            currency: currencyCode,
            value: product.price,
            items: items,
        });
    });
    setUpDynamicShippingCost(itemInfoUrl, detectCountry);
    $('.item-thumbnail .book-cover a').on('click', () => {
        track('Zoom', {
            on: 'Book cover',
            is_out_of_stock: isOutOfStock,
        });
    });
    $('.look-inside a').on('click', (evt) => {
        const elem = evt.currentTarget;
        const position = $(elem).data('position');
        track('Zoom', {
            on: 'Book page',
            is_out_of_stock: isOutOfStock,
        });
        gtag('event', 'select_content', {
            content_type: 'look_inside_preview',
            item_id: product.id,
            index: position,
        });
    });
    const relatedItems = document.querySelector('.related-items');
    if (relatedItems) {
        const itemListName = isOutOfStock ? 'Out-of-stock alternatives' : 'Similar books';
        // The item being viewed is the list's origin: with this every out-of-stock
        // page is its own item_list_id in GA4, so "which dead pages send readers to
        // a book they buy" is a direct query rather than a page-path inference.
        const itemListId = product.id;
        // https://fancyapps.com/carousel/get-started/usage/
        Carousel(relatedItems.querySelector('.f-carousel'), {
            transition: 'tween',
            infinite: false,
            on: {
                change: (instance) => {
                    track('Related Carousel', {
                        page_index: instance.getPageIndex(),
                        page_count: instance.getPages().length,
                        is_out_of_stock: isOutOfStock,
                    });
                },
            },
        }, { Arrows, Dots }).init();
        const relatedGa4Items = Array.from(document.querySelectorAll('.related-items a.item')).map((el) => ({
            item_id: el.dataset.ref,
            item_name: el.getAttribute('title') || undefined,
            index: el.dataset.position ? parseInt(el.dataset.position, 10) : undefined,
        }));
        gtag('event', 'view_item_list', {
            item_list_id: itemListId,
            item_list_name: itemListName,
            items: relatedGa4Items,
        });
        $('.related-items a.item').on('click', (evt) => {
            const elem = evt.currentTarget;
            const position = $(elem).data('position');
            track('Click Related Item', {
                is_out_of_stock: isOutOfStock,
                position,
            });
            gtag('event', 'select_item', {
                item_list_id: itemListId,
                item_list_name: itemListName,
                items: [{
                        item_id: $(elem).data('ref'),
                        index: position,
                    }],
            });
        });
    }
}
function setupItemListingLogging(itemListName, currencyCode, category) {
    const firstItem = document.querySelector('a.item');
    if (firstItem) {
        // Create the observer
        const observer = new IntersectionObserver((entries) => {
            // The callback receives an array of entries. We're only watching one.
            const entry = entries[0];
            if (!entry.isIntersecting) {
                return;
            }
            // Log impressions.
            logProductImpressions(document.querySelectorAll('a.item'), itemListName, currencyCode, category);
            // Stop observing the element after it has appeared
            observer.unobserve(entry.target);
        }, {
            // Optional configuration
            threshold: 0.9 // Run callback when 90% of the element is visible
        });
        // Start observing the target element.
        observer.observe(firstItem);
    }
}
function setUpDynamicShippingCost(itemInfoUrl, detectCountry) {
    const countryCodeElem = document.querySelector('select[name=country_code]');
    const shippingCostElem = document.getElementById('buy-now-shipping-cost');
    const totalCostElem = document.getElementById('buy-now-total-cost');
    if (!countryCodeElem || !shippingCostElem) {
        if (document.querySelector('.out-of-stock')) {
            // Perfectly normal not to have shipping for out of stock items.
            return;
        }
        Sentry.captureException(new Error('Shipping element not found in the DOM.'));
        return;
    }
    const refreshShippingInfo = (json) => {
        if (!json) {
            Sentry.captureException(new Error('Null shipping info returned.'));
            return;
        }
        shippingCostElem.innerHTML = json.shippingCost;
        // During A/B, the total cost may not be present.
        if (totalCostElem) {
            totalCostElem.innerHTML = json.totalCost;
        }
        $('select[name=country_code]').val(json.countryCode);
        const shipping = document.getElementById('estimated-shipping-dates');
        if (shipping) {
            shipping.innerHTML = json.estimatedArrivalHtml;
        }
        countryCodeElem.disabled = false;
    };
    $('select[name=country_code]').change((evt) => {
        const newCountryCode = $(evt.currentTarget).val();
        $.ajax({
            url: itemInfoUrl,
            data: { 'country': newCountryCode },
            success: refreshShippingInfo,
        });
        track('Change Shipping Country', {
            page: 'item',
            new_country: newCountryCode,
        });
    });
    if (detectCountry) {
        // Sets country code using GeoIP
        // Ideally do this server-side using https://nginx.org/en/docs/http/ngx_http_geoip_module.html
        countryCodeElem.disabled = true;
        $.get('https://ipinfo.io/json')
            .done((response) => {
            $.ajax({
                url: itemInfoUrl,
                data: { 'country': response.country },
                success: refreshShippingInfo,
                error: () => {
                    countryCodeElem.disabled = false;
                }
            });
        })
            .fail(() => {
            countryCodeElem.disabled = false;
        });
    }
    else {
        countryCodeElem.disabled = false;
    }
}
/**
 * cart.html
 * @param recaptchaSiteKey reCaptcha key.
 * @param products Products in the cart.
 * @param cartInfoUrl URL of the cart Ajax.
 * @param currencyCode Currency code (e.g. "EUR").
 */
export function runCart(recaptchaSiteKey, products, cartInfoUrl, currencyCode) {
    grecaptcha.ready(() => {
        grecaptcha.execute(recaptchaSiteKey, { action: 'cart' });
    });
    $('#shipping-amount .free-shipping-notice').hide();
    const compiledTemplate = Handlebars.compile(requiredQuerySelector('#item-template').innerHTML);
    const refreshCard = (result) => {
        for (const cartItem of result.cartItems) {
            // Hide the shipping fee not waivable (star) marker when the target country may not have
            // any item with waivable shipping fees (and we wouldn't show the notice).
            cartItem.shippingFeeNotWaivable =
                result.countryCode === 'FR' && !cartItem.isShippingFeeWaivable;
        }
        const rendered = compiledTemplate({ 'cartItems': result.cartItems });
        requiredQuerySelector('.books-list').innerHTML = rendered;
        requiredQuerySelector('#items-amount .cart-sub-total').innerHTML = result.itemsAmount;
        $('#shipping-amount select').val(result.countryCode);
        if (result.countryCode == 'FR' && !result.isShippingOffered) {
            $('#shipping-amount .free-shipping-notice').show(200);
        }
        else {
            $('#shipping-amount .free-shipping-notice').hide(200);
        }
        requiredQuerySelector('#shipping-amount .cart-sub-total').innerHTML =
            result.shippingAmount;
        requiredQuerySelector('#total-amount .total-price').innerHTML =
            result.totalAmount;
    };
    $.ajax({
        url: cartInfoUrl,
        success: refreshCard,
    });
    $('form.cart').on('click', 'button[name=remove_item]', (evt) => {
        const itemRef = $(evt.currentTarget).val();
        $.ajax({
            url: cartInfoUrl,
            data: { 'remove_item': itemRef },
            success: refreshCard,
        });
        umamiTrack('Remove from Cart', {
            currency: currencyCode,
            cart_item_count: products.length,
        });
        // Removing a Product from a Shopping Cart - Enhanced Ecommerce
        // https://developers.google.com/analytics/devguides/collection/ga4/ecommerce?client_type=gtag#add_or_remove_an_item_from_a_shopping_cart
        // https://developers.google.com/analytics/devguides/collection/ga4/reference/events?client_type=gtag#remove_from_cart
        let product;
        for (const productInCart of products) {
            if (productInCart.id == itemRef) {
                product = productInCart;
                break;
            }
        }
        if (product) {
            gtag('event', 'remove_from_cart', {
                currency: currencyCode,
                value: product.price,
                items: [convertProductToGA4Item(product)],
            });
        }
        evt.preventDefault();
    });
    $('#shipping-amount select').change((evt) => {
        const newCountryCode = $(evt.currentTarget).val();
        $.ajax({
            url: cartInfoUrl,
            data: { 'country_code': newCountryCode },
            success: refreshCard,
        });
        track('Change Shipping Country', {
            page: 'cart',
            new_country: newCountryCode,
            currency: currencyCode,
        });
    });
    // Style fixes (hack).
    $('#scroll_to_page_top').hide();
    umamiTrack('View Cart', {
        currency: currencyCode,
        cart_total: getProductsTotal(products),
        cart_items_count: products.length,
    });
    // Measuring a Checkout - Enhanced Ecommerce
    // https://developers.google.com/analytics/devguides/collection/ga4/reference/events?client_type=gtag#view_cart
    gtag('event', 'view_cart', {
        currency: currencyCode,
        value: getProductsTotal(products),
        items: convertProductsToGA4Items(products),
    });
    // Measuring Product Clicks.
    $('a.item').on('click', (evt) => {
        const elem = evt.currentTarget;
        // https://developers.google.com/analytics/devguides/collection/ga4/reference/events?client_type=gtag#view_item
        const ref = $(elem).data('ref');
        const name = elem.title;
        const price = $(elem).data('price');
        gtag('event', 'view_item', {
            currency: currencyCode,
            value: price,
            items: [{
                    item_id: ref,
                    item_name: name,
                    price: price,
                }],
        });
    });
    // Measuring a Checkout - Enhanced Ecommerce
    $('button[name=checkout]').on('click', (evt) => {
        umamiTrack('Checkout Intent', {
            variant: 'cart',
            currency: currencyCode,
            cart_total: getProductsTotal(products),
            cart_items_count: products.length,
        });
        // https://developers.google.com/analytics/devguides/collection/ga4/reference/events?client_type=gtag#begin_checkout
        gtag('event', 'begin_checkout', {
            currency: currencyCode,
            value: getProductsTotal(products),
            items: convertProductsToGA4Items(products),
        });
    });
}
export function runPaymentForm(cartItemsCount, freezeAmount, products, currencyCode, lang, initialShippingOffered) {
    track(freezeAmount ? 'View Payment Form' : 'View Custom Payment Form', {
        cart_items_count: cartItemsCount,
    });
    // Kept current for the add_shipping_info `shipping_tier` on submit; the
    // country_code change handler below refreshes it from /cart-info.
    let shippingOffered = initialShippingOffered;
    if (document.querySelector('p.nerr')) {
        track('Payment Form Validation Error');
    }
    const summaryButton = document.querySelector('#cart-summary button');
    if (summaryButton) {
        const content = document.querySelector('#cart-summary section');
        if (content) {
            summaryButton.addEventListener('click', () => {
                content.classList.toggle('is-open');
            });
        }
    }
    const countrySelect = document.querySelector('select[name="country_code"]');
    // Show the State / Province field only for countries whose addresses use one
    // (data-needs-state on each <option>). While hidden it is disabled so it is
    // neither submitted nor picked up by the browser's address autofill.
    const stateLi = document.querySelector('li.state-field');
    const stateInput = document.querySelector('input[name="state"]');
    const syncStateField = () => {
        if (!countrySelect || !stateLi || !stateInput) {
            return;
        }
        const needed = countrySelect.selectedOptions[0]?.dataset.needsState === '1';
        stateLi.classList.toggle('hidden', !needed);
        stateInput.disabled = !needed;
        stateInput.required = needed;
        if (!needed) {
            stateInput.value = '';
        }
    };
    syncStateField();
    if (countrySelect) {
        countrySelect.addEventListener('change', async () => {
            syncStateField();
            if (!freezeAmount) {
                return;
            }
            const countryCode = countrySelect.value;
            const response = await fetch(`/${lang}/cart-info?country_code=${countryCode}`);
            if (response.ok) {
                const cartInfo = await response.json();
                shippingOffered = !!cartInfo.isShippingOffered;
                const subtotalEl = document.getElementById('cart-subtotal');
                if (subtotalEl)
                    subtotalEl.innerHTML = cartInfo.itemsAmount;
                const shippingEl = document.getElementById('cart-shipping');
                if (shippingEl)
                    shippingEl.innerHTML = cartInfo.shippingAmount;
                const totalEl = document.getElementById('cart-total');
                if (totalEl)
                    totalEl.innerHTML = cartInfo.totalAmount;
                const totalHeaderEl = document.getElementById('cart-total-header');
                if (totalHeaderEl)
                    totalHeaderEl.innerHTML = cartInfo.totalAmount;
            }
        });
    }
    $('form.payment').on('submit', () => {
        let paymentMethod = '';
        document.querySelectorAll('input[name="payment_method"]').forEach((elm) => {
            if (elm.checked) {
                paymentMethod = elm.value;
            }
        });
        const paymentType = paymentTypeLabel(paymentMethod);
        const shippingCountry = $('select[name="country_code"]').val() ?? '';
        umamiTrack('Add Payment Info', {
            payment_type: paymentType,
            currency: currencyCode,
            cart_total: getProductsTotal(products),
            cart_items_count: products.length,
            shipping_country: shippingCountry,
        });
        // Single-page checkout: shipping address and payment method are
        // submitted together. Emit add_shipping_info first so GA4's ordered
        // Checkout journey report sees begin_checkout -> add_shipping_info ->
        // add_payment_info -> purchase. Like add_payment_info below, this fires
        // on every form.payment submit, including one the server later rejects
        // for a validation error.
        // https://developers.google.com/analytics/devguides/collection/ga4/reference/events?client_type=gtag#add_shipping_info
        gtag('event', 'add_shipping_info', {
            currency: currencyCode,
            value: getProductsTotal(products),
            shipping_tier: shippingOffered ? 'Free' : 'Standard',
            items: convertProductsToGA4Items(products),
        });
        // Measuring Purchases - Enhanced Ecommerce
        // https://developers.google.com/analytics/devguides/collection/ga4/ecommerce?client_type=gtag#initiate_the_checkout_process
        // https://developers.google.com/analytics/devguides/collection/ga4/reference/events?client_type=gtag#add_payment_info
        gtag('event', 'add_payment_info', {
            currency: currencyCode,
            value: getProductsTotal(products),
            payment_type: paymentType,
            items: convertProductsToGA4Items(products),
        });
    });
}
export function runPayPalReview(products, currencyCode) {
    track('View PayPal Review');
}
/**
 * payment-paypal-success.html
 * payment-lyra-success.html
 * @param transactionId Translation ID for purchases and refunds.
 * @param total Total transaction value (including tax and shipping).
 * @param shipping Shipping cost.
 * @param products Products in the cart.
 * @param currencyCode Currency code (e.g. "EUR").
 * @param paymentType Canonical payment-method label ("Card", "PayPal", "Bank transfer").
 */
export function logEcommercePurchase(transactionId, total, shipping, products, currencyCode, paymentType, submittedPurchase, sandbox) {
    if (submittedPurchase) {
        umamiTrack('Purchase Complete', {
            payment_type: paymentType,
            revenue: total,
            currency: currencyCode,
            cart_items_count: products.length,
            shipping,
            sandbox,
        });
    }
    // Measuring Purchases - Enhanced Ecommerce
    // https://developers.google.com/tag-manager/enhanced-ecommerce
    // https://developers.google.com/analytics/devguides/collection/ga4/ecommerce?client_type=gtag#initiate_the_checkout_process
    gtag('event', 'purchase', {
        transaction_id: transactionId,
        currency: currencyCode,
        value: total,
        shipping: shipping,
        payment_type: paymentType,
        items: convertProductsToGA4Items(products),
    });
}
/**
 * email-form.html
 * @param recaptchaSiteKey reCaptcha key.
 */
export function runEmailForm(recaptchaSiteKey, cartItemsCount) {
    track('View Email Form', {
        cart_items_count: cartItemsCount,
    });
    const form = document.getElementById('email-form');
    const submitButton = document.getElementById('email-submit');
    const captchaInput = document.getElementById('email-captcha');
    if (form && submitButton && captchaInput) {
        // Enable the button since we will handle reCAPTCHA on submit.
        submitButton.disabled = false;
        form.addEventListener('submit', (e) => {
            e.preventDefault();
            submitButton.disabled = true;
            if (typeof grecaptcha === 'undefined') {
                form.submit();
                return;
            }
            grecaptcha.ready(() => {
                grecaptcha.execute(recaptchaSiteKey, { action: 'email' }).then((token) => {
                    if (token) {
                        captchaInput.value = token;
                    }
                    track('Send Email', {
                        cart_items_count: cartItemsCount,
                    });
                    form.submit();
                }, () => {
                    track('Send Email', {
                        cart_items_count: cartItemsCount,
                    });
                    form.submit();
                });
            });
        });
    }
}
/**
 * error404.html
 * @param recaptchaSiteKey reCaptcha key.
 * @param isSystemTest Whether this is a system test.
 * @param reportUrl URL to ping.
 */
export function reportError404(recaptchaSiteKey, isSystemTest, reportUrl) {
    grecaptcha.ready(() => {
        const reportError = (token) => {
            const url = new URL(reportUrl);
            url.searchParams.append('token', token);
            fetch(url.toString()).then(() => {
                console.info('Logged 404.');
            });
        };
        if (isSystemTest) {
            reportError('system-test');
        }
        else {
            grecaptcha.execute(recaptchaSiteKey, {
                action: '404',
            }).then((token) => {
                reportError(token);
            });
        }
    });
}
